/**
 * Calendar-day boundaries in the property's timezone.
 *
 * CLAUDE.md: every DB timestamp is UTC, and every human-facing boundary is the
 * property's local one. Those disagree for most of the day, so "today" can never be
 * derived from the server process clock — a container running UTC would roll the day
 * over an hour early for a London property in summer, and a night porter filing at
 * 00:30 would see their ticket land on yesterday's sheet.
 */
/** Formats an instant as the property's calendar date, `YYYY-MM-DD`. */
export function propertyDateKey(instant, timeZone) {
    // en-CA renders ISO-order dates, which is the only reason it is used here.
    return new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(instant);
}
/**
 * The UTC instant at which the property's calendar day containing `instant` began.
 *
 * Works by guessing UTC midnight for that date, measuring how far the guess lands
 * from the zone's wall clock, and stepping back by that offset.
 */
export function startOfPropertyDay(instant, timeZone) {
    return midnightUtcFor(propertyDateKey(instant, timeZone), timeZone);
}
/**
 * Start of the day *after* the one containing `instant`.
 *
 * Ranges are half-open — `createdAt >= start && createdAt < end` — rather than
 * closing at 23:59:59.999. A millisecond-wide gap is still a gap, and Postgres
 * timestamps carry microseconds, so a row landing inside it would vanish from every
 * report that claimed to cover the day.
 */
export function endOfPropertyDay(instant, timeZone) {
    const start = startOfPropertyDay(instant, timeZone);
    return midnightUtcFor(nextDateKey(start, timeZone), timeZone);
}
/** Half-open `[start, end)` covering the property day that contains `instant`. */
export function propertyDayRange(instant, timeZone) {
    return {
        start: startOfPropertyDay(instant, timeZone),
        end: endOfPropertyDay(instant, timeZone),
    };
}
/**
 * Half-open range spanning the property days containing the instants `from` through
 * `to`, inclusive of both.
 *
 * Takes **instants**. If your bounds are calendar strings — a query param, a date
 * input — reach for `propertyRangeBetweenKeys` instead and do not convert them
 * yourself: `new Date('2026-08-01T00:00:00Z')` is still 31 July everywhere behind
 * UTC, so the range would quietly begin a day early.
 */
export function propertyRangeBetween(from, to, timeZone) {
    const [earlier, later] = from <= to ? [from, to] : [to, from];
    return {
        start: startOfPropertyDay(earlier, timeZone),
        end: endOfPropertyDay(later, timeZone),
    };
}
/**
 * Half-open range spanning `fromKey` through `toKey`, both inclusive `YYYY-MM-DD`
 * calendar dates in `timeZone`.
 *
 * This is the safe way to turn user-supplied dates into a query window: the keys are
 * resolved against the zone directly, so there is no instant in the middle for an
 * offset to push onto the wrong side of midnight.
 */
export function propertyRangeBetweenKeys(fromKey, toKey, timeZone) {
    // Lexicographic comparison is sound for zero-padded ISO dates.
    const [earlier, later] = fromKey <= toKey ? [fromKey, toKey] : [toKey, fromKey];
    return {
        start: midnightUtcFor(earlier, timeZone),
        end: midnightUtcFor(nextDateKey(midnightUtcFor(later, timeZone), timeZone), timeZone),
    };
}
/** Half-open `[start, end)` for the single property day named by `dateKey`. */
export function propertyDayFromKey(dateKey, timeZone) {
    return propertyRangeBetweenKeys(dateKey, dateKey, timeZone);
}
/** The calendar date following the property day that starts at `dayStart`. */
function nextDateKey(dayStart, timeZone) {
    // Step from the following midday rather than midnight, so a 23- or 25-hour DST day
    // still advances exactly one calendar date rather than sticking or skipping.
    return propertyDateKey(new Date(dayStart.getTime() + 36 * 60 * 60 * 1000), timeZone);
}
/**
 * Resolves `YYYY-MM-DD` in `timeZone` to the UTC instant of its midnight.
 *
 * Two passes, and the second is not optional. The first can only sample the offset
 * at *UTC* midnight, which in Auckland or Honolulu sits half a day from the local
 * midnight being solved for. On a DST transition date the changeover falls between
 * those two instants, so the first pass applies the wrong side of the jump and
 * returns a "start of day" that lands in the previous day — Auckland's 27 Sep 2026
 * came back as 26 Sep 23:00 NZST. Re-sampling at the candidate and re-applying
 * converges, because the candidate is by then within an hour of the true answer.
 *
 * On a spring-forward date where 00:00 does not exist, this lands on the first
 * instant that does, which is the intended reading of "start of day".
 */
function midnightUtcFor(dateKey, timeZone) {
    const guess = new Date(`${dateKey}T00:00:00Z`);
    const candidate = new Date(guess.getTime() - zoneOffsetMs(guess, timeZone));
    const twoPass = new Date(guess.getTime() - zoneOffsetMs(candidate, timeZone));
    if (isFirstInstantOfDate(twoPass, dateKey, timeZone))
        return twoPass;
    // Havana and Santiago spring forward *at* midnight, so that wall-clock time does
    // not exist and no offset arithmetic can land on it. Scan a window around the
    // estimate and take the earliest instant genuinely on the date: for a gap that is
    // 01:00 local, and for an autumn overlap — where midnight happens twice — it is
    // the first of the two, which is where the day actually began.
    const step = 15 * 60 * 1000;
    const hour = 60 * 60 * 1000;
    for (let t = twoPass.getTime() - 3 * hour; t <= twoPass.getTime() + 3 * hour; t += step) {
        if (propertyDateKey(new Date(t), timeZone) === dateKey)
            return new Date(t);
    }
    return twoPass;
}
/** True when `instant` is on `dateKey` and the millisecond before it is not. */
function isFirstInstantOfDate(instant, dateKey, timeZone) {
    return (propertyDateKey(instant, timeZone) === dateKey &&
        propertyDateKey(new Date(instant.getTime() - 1), timeZone) !== dateKey);
}
/** How far ahead of UTC `timeZone` sits at `instant`, in milliseconds. */
function zoneOffsetMs(instant, timeZone) {
    const asZoned = new Date(instant.toLocaleString('en-US', { timeZone }));
    const asUtc = new Date(instant.toLocaleString('en-US', { timeZone: 'UTC' }));
    return asZoned.getTime() - asUtc.getTime();
}
//# sourceMappingURL=property-day.js.map